/**
 * multiAgentPostSaveAnalysis — the multi-agent equivalent of the dev-agent
 * `runDevAgentPostSaveAnalysis` (pages/dev-agent/devAgentPostSaveAnalysis.ts).
 *
 * Differences from the dev-agent original (per the shared contract):
 *   - POST /api/runs/{runId}/analyze instead of the dev-agent session analyze
 *   - results populate useMultiAgentIdeStore (typed slices) instead of
 *     useDevAgentStore feedItems — and they ALSO arrive over SSE (MA-7), so the
 *     synchronous body here is a best-effort fast-path; SSE is the source of truth
 *   - auto-queues run-test for up to 2 unique dependent test classes via
 *     useMultiAgentIdeStore.runTest (MA-10)
 */
import {
  runMultiAgentPostSaveAnalysis,
  isWordChar,
  isAlphanumeric,
  type MultiAgentPostSaveAnalysisResult,
} from "@/services/multi-agent";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";

const MAX_AUTO_TEST_RUNS = 2;

/**
 * True when "Test" or "Tests" appears as a whole word — i.e. it ends at a word
 * boundary: the char right after must be the end of the string or a non-word
 * char (word char = letter, digit, or "_"). Examples: "FooTest" / "FooTests" /
 * "Test_X" match; "Testing" does NOT.
 */
function looksLikeTestClass(name: string): boolean {
  let from = 0;
  for (;;) {
    const i = name.indexOf("Test", from);
    if (i < 0) return false;
    const afterTest = i + 4; // index right after "Test"
    // "Test\b": end-of-string or non-word char immediately after "Test".
    if (afterTest >= name.length || !isWordChar(name[afterTest])) return true;
    // "Tests\b": the word char after "Test" is "s", then end/non-word.
    if (name[afterTest] === "s") {
      const afterTests = afterTest + 1;
      if (afterTests >= name.length || !isWordChar(name[afterTests])) return true;
    }
    from = i + 1;
  }
}

/**
 * Strip a trailing ".<alnum>" extension. Only strips when there is a "." after
 * the last "/" and everything after that last "." is one or more alphanumeric
 * chars; otherwise the string is returned unchanged.
 */
function stripTrailingExtension(value: string): string {
  const dot = value.lastIndexOf(".");
  if (dot < 0 || dot <= value.lastIndexOf("/")) return value;
  const ext = value.slice(dot + 1);
  if (ext.length === 0) return value; // nothing after the dot, needs at least one char
  for (const ch of ext) {
    if (!isAlphanumeric(ch)) return value; // a non-alnum char after the dot -> unchanged
  }
  return value.slice(0, dot);
}

/** Auto-queue run-test for up to 2 unique dependent test classes (MA-10). */
async function queuePostSaveTests(testClassNames: string[]): Promise<void> {
  if (testClassNames.length === 0) return;
  const store = useMultiAgentIdeStore.getState();
  if (store.runningTest) return;
  const unique = [...new Set(testClassNames.filter(Boolean))].slice(0, MAX_AUTO_TEST_RUNS);
  for (const testClass of unique) {
    try {
      await useMultiAgentIdeStore.getState().runTest(testClass, { announceStart: false });
    } catch {
      /* store.runTest already records failures */
    }
  }
}

/**
 * Populate the IDE store from the analyze response and auto-queue dependent
 * tests. Results also stream over SSE (impact_analysis / test_coverage /
 * dependencies_ready) — store upserts are idempotent by class/target, so the
 * fast-path body and the SSE converge without duplication.
 */
function applyResults(
  data: { class_name: string },
  results: MultiAgentPostSaveAnalysisResult,
): void {
  const ide = useMultiAgentIdeStore.getState();

  if (results.dependencies && results.dependencies.target) {
    const dep = results.dependencies;
    const callers = (dep.callers || []).map((c) => ({
      name: c.name,
      id: String(c.id ?? ""),
      metadata_type: c.metadata_type || "ApexClass",
    }));
    const tests = (dep.tests || []).map((t) => ({
      name: t.name,
      id: String(t.id ?? ""),
      metadata_type: t.metadata_type || "ApexClass",
    }));
    ide.addDependency({
      target: dep.target,
      target_id: dep.target_id ?? null,
      callers,
      tests,
      total: dep.total ?? callers.length + tests.length,
      status: "awaiting_user_scope",
      captured_at: dep.captured_at || new Date().toISOString(),
    });
  }

  if (results.impact) {
    ide.addImpactAnalysis({
      class_name: data.class_name,
      blast_radius: results.impact.blast_radius,
      callers: results.impact.callers,
      callees: results.impact.callees,
      data_access: results.impact.data_access,
      triggers_at_risk: results.impact.triggers_at_risk,
      references: results.impact.references,
      summary: results.impact.summary,
    });
  }

  if (results.coverage) {
    ide.addTestCoverage({
      class_name: data.class_name,
      test_classes: results.coverage.test_classes,
      aggregate_coverage_pct: results.coverage.aggregate_coverage_pct,
      summary: results.coverage.summary,
    });
  }

  const autoTests = [
    ...(results.dependencies?.tests?.map((t) => t.name) || []),
    ...(results.coverage?.test_classes?.map((t) => t.name) || []),
  ].filter((name) => !!name && looksLikeTestClass(name) && name !== data.class_name);
  void queuePostSaveTests(autoTests);
}

export async function runMultiAgentPostSaveAnalysisFlow(
  runId: string,
  data: { fileName: string; metadataType: string },
): Promise<void> {
  // The backend validates class_name against ^[A-Za-z][A-Za-z0-9_]{0,79}$ (no
  // extension / punctuation) and 400s otherwise — strip the metadata extension
  // (e.g. "Foo.cls" -> "Foo") so analyze always gets a bare class name.
  const className = stripTrailingExtension(data.fileName).trim();
  if (!className) return;
  const results = await runMultiAgentPostSaveAnalysis(runId, {
    class_name: className,
    metadata_type: data.metadataType,
  });
  // The backend may return an empty body and stream the results over SSE
  // instead — only apply the synchronous fast-path when it actually carried data.
  if (results && (results.impact || results.coverage || results.dependencies)) {
    applyResults({ class_name: className }, results);
  }
}
